"use client";

import { useState, useEffect, useRef } from "react";
import { MessageCircle, Send } from "lucide-react";

interface Message {
  content: string;
  type: "bot" | "user";
  id: number;
}

const steps = [
  {
    content: `The topics discussed below might be new to you as a retail investor, but they're part of the portfolio for almost every institutional investor, HNI, and wealthy Family Office.
  
  Most of them used these products for tax efficiency until 2023. After 2023, taxation is applicable as per Fixed income products.`,
    button: "Why not retail investors?",
  },
  {
    content: `Because complexity in understanding and minimum ticket size are the main reasons. Previously, ₹25 Lakhs was the minimum investment to invest in this category.`,
    button: "Got It, Let's Go!",
  },
  {
    content: `You are going to learn about MLDs (Market Linked Debentures)
  
  Why this product is so good, if you understand the concept`,
    button: "Explain MLD in simple words",
  },
  {
    content: `Debt investments are known for protecting capital, while equity investments aim to grow it. What if there was an investment option that could offer both capital protection and potential growth? Enter Market-Linked Debentures (MLDs)`,
    button: "Explain with a Real Example",
  },
  {
    content: `Ok, let's start with our new product offering which will give you a better idea.
  We are launching 2 MLDs, one is linked with Nifty 50 and another with Gold MCX.
  
  Now I'm going to explain the MLD with Nifty 50 as the underlying asset.`,
    button: "Ok",
  },
  {
    content: `Here's the product:
  
  1. Coupon Payment Linked to the performance of the Nifty 50 Index
  2. 165% return or 1.65 times returns of Nifty Upside
  3. Principal Protection up to -20% fall in the Nifty 50 index at maturity
  4. Tenure 3 years`,
    button: "Explain in detail",
  },
  {
    content: `Let's say you invested ₹1 Lakh in this MLD. After maturity, there will be 2 scenarios, whether Nifty can go up from today's value or go down.
  
  Let's take an Upside:
  If Nifty goes up 35% in this period, then your returns will be 1.65 times of Nifty return i.e., 57.75%`,
    button: "What if Nifty goes down in this period?",
  },
  {
    content: `There are two main types of MLDs:
  
  1. Principal Protected
  2. Non-Principal Protected
  
  The current product is designed with partial principal protection`,
    button: "What does that mean?",
  },
  {
    content: `If Nifty goes down up to -20%, your principal is 100% protected.
  
  If it crosses that level, then the principal amount is also impacted. For example: if Nifty goes down -25%, then your portfolio also goes down by 25%`,
    button: "I'm still confused, can you clarify with an example?",
  },
  {
    content: `Assume: Your friend invested ₹1 Lakh in a Nifty 50 Index fund.
  You invested ₹1 Lakh in an MLD with Nifty 50 as the underlying.
  
  Fast forward 3 Years:
  
  Positive case:
  Nifty up by 35%, so your friend's return will be approximately: ₹1,35,000 i.e., 35% return
  
  In your case, your returns will be 1.65 times of Nifty i.e., your ₹1 Lakh becomes ₹1,57,000`,
    button: "What about the negative case?",
  },
  {
    content: `Negative case:
  Nifty down by 20%, your friend's investment value becomes ₹80,000 (20% loss)
  
  In your case, up to -20% your principal is protected, so your portfolio remains at ₹1,00,000
  
  If Nifty is -20% even after 3 years, the market is at a discount. You can redeem this ₹1 Lakh and invest the same amount in an Index fund, so now you will have more units than your friend.`,
    button: "Is it too good to believe?",
  },
  {
    content: `I know, let me explain how it works in the backend. There's no magic here.
  
  Hope you know Hybrid mutual funds. Mostly low risk appetite users or senior citizens invest in these hybrid funds because whenever the market crashes, these funds don't fall much. This is because the fund manager invests our money in a combination of Bonds (debt instruments) and Stocks.
  
  When the market crashes, the stock component will go down, but the bond component is not affected, so it will fall less than a pure equity fund.`,
    button: "Yeah, I know",
  },
  {
    content: `Similarly, the MLD is also designed with a combination of Bonds and Options (F&O).
  
  Let's say, if you invest ₹1 Lakh, the MLD manager might design a system like this (just an example):
  
  He will invest ₹80,000 in bonds, so that it's not market-linked.
  The remaining ₹20K will be invested in corresponding index Options as per the return strategy illustrated.
  
  In the Nifty down case scenario, if Nifty falls, assume the ₹20K might become zero. But the remaining ₹80K with 9% annualized returns becomes ₹1 Lakh after 3 years, which is equal to your principal amount.
  
  That's it.`,
    button: "Important Learning",
  },
  {
    content: `Many of your friends might be doing F&O daily trading and losing money. But that's not the purpose why F&O exists. Knowledgeable people designed these kinds of products to maximize returns and also protect capital.
  
  Share this with your trader-addicted friend - either they might complicate things for you or find ways to insult me 😄`,
    button: "How can I Invest?",
  },
  {
    content: `There are many financial institutions offering MLDs like Edelweiss, Incred, Tata Capital, Aditya Birla, but currently they are offering for HNIs.
  
  We are designing the same products by partnering with Incred, Aditya Birla, and Edelweiss with a low ticket size i.e., currently ₹1 Lakh.`,
    button: "Is it Regulated?",
  },
  {
    content: `Yes, same like Mutual funds, only the authorized SEBI institutions can launch MLDs and they are highly regulated by SEBI.
  
  One needs a demat account to invest in MLDs, and you can track your MLD from your demat account. After the tenure, as per the rules, the amount is automatically credited to your registered bank account.`,
    button: "I want to Invest / I have queries",
  },
];

const formatContent = (content: string) => {
  return content
    .split("\n\n")
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      // Format numbered lists with proper indentation and styling
      paragraph = paragraph.replace(
        /^\d+\.\s+(.*)/gm,
        (match) => `<div class="flex items-start gap-3 mb-2">
          <span class="text-blue-400 font-semibold min-w-[1.25rem] flex-shrink-0">${
            match.match(/^\d+\./)[0]
          }</span>
          <span>${match.replace(/^\d+\.\s+/, "")}</span>
        </div>`
      );

      // Format bullet points with proper indentation and styling
      paragraph = paragraph.replace(
        /^•\s+(.*)/gm,
        (match) => `<div class="flex items-start gap-3 mb-2">
          <span class="text-blue-400 font-semibold min-w-[1.25rem] flex-shrink-0">•</span>
          <span>${match.replace(/^•\s+/, "")}</span>
        </div>`
      );

      return `<div class="mb-4 last:mb-0 leading-relaxed whitespace-pre-wrap text-left">${paragraph}</div>`;
    })
    .join("");
};

export default function MLDChatBot() {
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentStep === 0) {
      addBotMessage(steps[0].content);
    }
  }, [currentStep]);

  const addBotMessage = async (content: string) => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setMessages((prev) => [...prev, { content, type: "bot", id: Date.now() }]);
    setIsTyping(false);
  };

  const handleNext = async () => {
    const userResponse = steps[currentStep].button;
    setMessages((prev) => [
      ...prev,
      { content: userResponse, type: "user", id: Date.now() },
    ]);

    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await addBotMessage(steps[nextStep].content);
    }
  };

  const handleWhatsAppContact = () => {
    window.open("https://wa.me/918801648801", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      {/* Header */}
      <header className="bg-[#2D2D2D] border-b border-gray-700 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-blue-400" />
          <h1 className="text-xl font-bold text-white">Senior Secured Bonds</h1>
        </div>
      </header>

      {/* Chat Container */}
      <main className="flex-1 overflow-hidden flex flex-col max-w-4xl w-full mx-auto">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-6 space-y-6 px-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-6 py-4 ${
                  message.type === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-[#2D2D2D] text-gray-100"
                }`}
              >
                <div
                  className="prose prose-lg prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: formatContent(message.content),
                  }}
                />
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex items-center gap-2 text-gray-400">
              <div className="flex gap-1">
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0s" }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
              <span className="text-sm">Assistant is typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-700 p-4 sticky bottom-0 bg-[#1A1A1A]">
          <div className="max-w-4xl mx-auto flex gap-4">
            <button
              onClick={handleNext}
              disabled={currentStep >= steps.length - 1 && !isTyping}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep < steps.length
                ? steps[currentStep].button
                : "Chat Completed"}
            </button>
            {currentStep >= steps.length - 1 && (
              <button
                onClick={handleWhatsAppContact}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                Contact on WhatsApp
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
